import React, { Component, useEffect, useState, useMemo } from 'react';
import PageWrapper from '../../custom-components/wrappers/page-wrapper/PageWrapper';
import MainDashboardWrapper from '../../components/MainDashboardWrapper/MainDashboardWrapper';
import SettingsLayoutWrapper from '../../custom-components/wrappers/settings-layout-wrapper/SettingsLayoutWrapper';
import { connect } from 'react-redux';
import { compose } from 'redux';
import css from './CoachBrandingPage.css';
import config from '../../config';
import { getAllMyActivities } from '../../custom-ducks/myActivities.duck';
import { sendTestCoachEmail } from '../../custom-ducks/settings.duck';
import ReactQuill from 'react-quill';
import { decode } from 'html-entities';
import { Loader } from 'react-feather';
import { IconSpinner } from '../../components';
import { notifySuccess } from '../../components/HotToast/HotToastProvider';
import UpgradeGate from '../../custom-components/elements/upgrade-gate/UpgradeGate';
import { meetsTierGate, TIERS } from '../../util/coachTier';

// === Promo destination types ===
// What "what does this link to?" dropdown lets coaches pick. The actual URL
// is resolved at save time so the dropdown stays human-readable in the form,
// and the backend just receives a plain ctaUrl it doesn't have to interpret.
const CTA_TYPES = {
  PROFILE: 'profile',
  DISCOVERY_CALL: 'discoveryCall',
  CUSTOM: 'custom',
};

// === Templates ===
// One-click presets that pre-fill title / body / ctaLabel / ctaType.
// Every template defaults to a destination that keeps clients inside Viveka.
const TEMPLATES = [
  {
    key: 'discoveryCall',
    label: 'Discovery call',
    description: 'Highest-converting CTA in coaching. Stays inside Viveka.',
    promo: {
      title: "Not ready to commit? Let's have a 15-minute chat.",
      body: "A free, no-pressure conversation to see if we're a good fit. Pick a time that works for you.",
      ctaLabel: 'Book a chat',
      ctaType: CTA_TYPES.DISCOVERY_CALL,
    },
  },
  {
    key: 'fullMenu',
    label: 'My full coaching menu',
    description: 'Soft sell. Shows everything you currently offer.',
    promo: {
      title: 'Looking for more support?',
      body: "Here's everything I currently offer — from intensive programs to ongoing one-to-one coaching.",
      ctaLabel: "See what's available",
      ctaType: CTA_TYPES.PROFILE,
    },
  },
  {
    key: 'testimonials',
    label: 'Read client testimonials',
    description: 'Trust-builder. Sends people to your verified testimonials.',
    promo: {
      title: 'Curious what others say?',
      body: "Read how working together has changed things for the people I've coached.",
      ctaLabel: 'Read testimonials',
      ctaType: CTA_TYPES.PROFILE,
    },
  },
  {
    key: 'program',
    label: 'A specific program',
    description: 'Link directly to one of your programs. Highest direct-conversion path.',
    promo: {
      title: 'Ready to go deeper?',
      body: "I've got a program that might be the right next step. Take a look.",
      ctaLabel: 'See the program',
      ctaType: CTA_TYPES.CUSTOM,
    },
  },
];

const DEFAULT_ACCENT = '#14B8A6';

const resolveCtaUrl = (ctaType, customUrl, currentUser) => {
  const userId = currentUser?.id?.uuid;
  const root = (config.canonicalRootURL || '').replace(/\/$/, '');
  switch (ctaType) {
    case CTA_TYPES.PROFILE:
    case CTA_TYPES.DISCOVERY_CALL:
      return userId ? `${root}/u/${userId}` : '';
    case CTA_TYPES.CUSTOM:
      return (customUrl || '').trim();
    default:
      return '';
  }
};

const hydratePromo = (stored) => {
  if (!stored) {
    return { isLegacy: false, title: '', body: '', ctaLabel: '', ctaType: '', customUrl: '', accent: DEFAULT_ACCENT };
  }
  if (typeof stored === 'string') {
    return { isLegacy: true, title: '', body: '', ctaLabel: '', ctaType: '', customUrl: '', accent: DEFAULT_ACCENT };
  }
  return {
    isLegacy: false,
    title: stored.title || '',
    body: stored.body || '',
    ctaLabel: stored.ctaLabel || '',
    ctaType: stored.ctaType || (stored.ctaUrl ? CTA_TYPES.CUSTOM : ''),
    customUrl: stored.ctaType === CTA_TYPES.CUSTOM ? (stored.ctaUrl || '') : '',
    accent: stored.accent || DEFAULT_ACCENT,
  };
};

const CoachBranding = ({ getSdk, currentUser, onSendTestCoachEmail }) => {
  const [emailSending, setEmailSending] = useState(false);
  const [imageUrl, setImageUrl] = useState();
  const [footer, setFooter] = useState();

  const [promoIsLegacy, setPromoIsLegacy] = useState(false);
  const [promoTitle, setPromoTitle] = useState('');
  const [promoBody, setPromoBody] = useState('');
  const [promoCtaLabel, setPromoCtaLabel] = useState('');
  const [promoCtaType, setPromoCtaType] = useState('');
  const [promoCustomUrl, setPromoCustomUrl] = useState('');
  const [promoAccent, setPromoAccent] = useState(DEFAULT_ACCENT);

  const sdk = getSdk();

  useEffect(() => {
    const eb = currentUser?.attributes?.profile?.privateData?.emailBrand;
    setImageUrl(eb?.logo);
    setFooter(eb?.footer);
    const h = hydratePromo(eb?.promo);
    setPromoIsLegacy(h.isLegacy);
    setPromoTitle(h.title);
    setPromoBody(h.body);
    setPromoCtaLabel(h.ctaLabel);
    setPromoCtaType(h.ctaType);
    setPromoCustomUrl(h.customUrl);
    setPromoAccent(h.accent);
  }, [currentUser]);

  const buildPromoPayload = () => {
    const hasAnything =
      (promoTitle || '').trim() ||
      (promoBody || '').trim() ||
      ((promoCtaLabel || '').trim() && promoCtaType);
    if (!hasAnything) return null;
    return {
      title: (promoTitle || '').trim() || undefined,
      body: (promoBody || '').trim() || undefined,
      ctaLabel: (promoCtaLabel || '').trim() || undefined,
      ctaType: promoCtaType || undefined,
      ctaUrl: resolveCtaUrl(promoCtaType, promoCustomUrl, currentUser) || undefined,
      accent: promoAccent || undefined,
    };
  };

  const save = () => {
    return sdk.currentUser.updateProfile({
      privateData: {
        emailBrand: {
          logo: imageUrl,
          promo: buildPromoPayload(),
          footer: footer,
        },
      },
    });
  };

  const applyTemplate = (tpl) => {
    setPromoTitle(tpl.promo.title || '');
    setPromoBody(tpl.promo.body || '');
    setPromoCtaLabel(tpl.promo.ctaLabel || '');
    setPromoCtaType(tpl.promo.ctaType || '');
    setPromoIsLegacy(false);
  };

  const clearPromo = () => {
    setPromoTitle('');
    setPromoBody('');
    setPromoCtaLabel('');
    setPromoCtaType('');
    setPromoCustomUrl('');
    setPromoAccent(DEFAULT_ACCENT);
    setPromoIsLegacy(false);
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ['bold', 'italic', 'underline', 'strike', 'blockquote'],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link'],
      ['clean'],
    ],
  };

  const quillFormats = [
    'header','bold','italic','underline','strike','blockquote',
    'list','bullet','indent','link',
  ];

  const canAccessBranding = meetsTierGate(currentUser, TIERS.VERIFIED);
  if (!canAccessBranding) {
    return (
      <PageWrapper>
        <MainDashboardWrapper title={'Coach Branding'} page={'coach-branding'}>
          <SettingsLayoutWrapper page="branding">
            <UpgradeGate
              featureName="Coach Branding"
              requiredTier={TIERS.VERIFIED}
              currentUser={currentUser}
              benefits={[
                'Upload your own logo for client-facing emails',
                'Customise the promo banner shown in confirmations',
                'Add a personal email footer with your contact details',
                'Build a recognisable coaching practice',
              ]}
            />
          </SettingsLayoutWrapper>
        </MainDashboardWrapper>
      </PageWrapper>
    );
  }

  const previewCtaUrl = resolveCtaUrl(promoCtaType, promoCustomUrl, currentUser);

  return (
    <PageWrapper>
      <MainDashboardWrapper title={'Coach Branding'} page={'coach-branding'}>
        <SettingsLayoutWrapper page="branding">
          <div className={css.base}>

            {/* ── Logo ── */}
            <div className={css.settingsBlock}>
              <div className={css.blockHeader}>
                <p className={css.blockTitle}>
                  <span className={css.blockIcon}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="18" height="18" rx="2"/>
                      <circle cx="8.5" cy="8.5" r="1.5"/>
                      <polyline points="21 15 16 10 5 21"/>
                    </svg>
                  </span>
                  Email Logo
                </p>
              </div>
              <div className={css.blockBody}>
                {imageUrl && <img src={imageUrl} alt="Logo preview" className={css.logoPreview} />}
                <p className={css.fieldHint}>Upload a PNG or JPEG — displayed at the top of every client email.</p>
                <input
                  type="file"
                  className={css.fileInput}
                  onChange={e => {
                    const file = e.target.files[0];
                    sdk.images
                      .upload({ image: file }, { expand: true })
                      .then(res => {
                        const image = res?.data?.data?.attributes?.variants?.default?.url;
                        setImageUrl(image);
                      });
                  }}
                  accept="image/png, image/jpeg"
                />
              </div>
            </div>

            {/* ── Email Footer ── */}
            <div className={css.settingsBlock}>
              <div className={css.blockHeader}>
                <p className={css.blockTitle}>
                  <span className={css.blockIcon}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="8" y1="6" x2="21" y2="6"/>
                      <line x1="8" y1="12" x2="21" y2="12"/>
                      <line x1="8" y1="18" x2="21" y2="18"/>
                      <line x1="3" y1="6" x2="3.01" y2="6"/>
                      <line x1="3" y1="12" x2="3.01" y2="12"/>
                      <line x1="3" y1="18" x2="3.01" y2="18"/>
                    </svg>
                  </span>
                  Email Footer
                </p>
              </div>
              <div className={css.blockBody}>
                <div className={css.quillWrap}>
                  <ReactQuill
                    value={decode(footer)}
                    onChange={value => setFooter(value)}
                    modules={quillModules}
                    formats={quillFormats}
                  />
                </div>
              </div>
            </div>

            {/* ── Promo Banner ── */}
            <div className={css.settingsBlock}>
              <div className={css.blockHeader}>
                <p className={css.blockTitle}>
                  <span className={css.blockIcon}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                    </svg>
                  </span>
                  Promo Banner
                </p>
              </div>
              <div className={css.blockBody}>
                <p className={css.introText}>
                  A small card shown at the bottom of every client email — perfect for inviting them to your next step.
                </p>

                {promoIsLegacy && (
                  <div className={css.legacyNotice}>
                    Your previous promotion was a free-text block. When you save here, it'll be replaced with the new card format below.
                  </div>
                )}

                <p className={css.sectionLabel}>Quick start</p>
                <div className={css.templateRow}>
                  {TEMPLATES.map(tpl => (
                    <button
                      key={tpl.key}
                      type="button"
                      onClick={() => applyTemplate(tpl)}
                      title={tpl.description}
                      className={css.templateBtn}
                    >
                      {tpl.label}
                    </button>
                  ))}
                  {(promoTitle || promoBody || promoCtaLabel) && (
                    <button type="button" onClick={clearPromo} className={css.clearBtn}>
                      Clear
                    </button>
                  )}
                </div>

                <div className={css.fieldGroup}>
                  <label className={css.fieldLabel}>Headline</label>
                  <input
                    type="text"
                    className={css.input}
                    value={promoTitle}
                    onChange={e => setPromoTitle(e.target.value)}
                    placeholder="Not ready to commit? Let's have a 15-minute chat."
                    maxLength={120}
                  />
                </div>

                <div className={css.fieldGroup}>
                  <label className={css.fieldLabel}>Body</label>
                  <textarea
                    rows={3}
                    className={css.textarea}
                    value={promoBody}
                    onChange={e => setPromoBody(e.target.value)}
                    placeholder="A free, no-pressure conversation to see if we're a good fit."
                    maxLength={300}
                  />
                </div>

                <div className={css.twoCol}>
                  <div>
                    <label className={css.fieldLabel}>Button label</label>
                    <input
                      type="text"
                      className={css.input}
                      value={promoCtaLabel}
                      onChange={e => setPromoCtaLabel(e.target.value)}
                      placeholder="Book a chat"
                      maxLength={40}
                    />
                  </div>
                  <div>
                    <label className={css.fieldLabel}>Where does it link?</label>
                    <select
                      className={css.select}
                      value={promoCtaType}
                      onChange={e => setPromoCtaType(e.target.value)}
                    >
                      <option value="">— Choose —</option>
                      <option value={CTA_TYPES.PROFILE}>My Viveka profile</option>
                      <option value={CTA_TYPES.DISCOVERY_CALL}>A discovery call (my profile)</option>
                      <option value={CTA_TYPES.CUSTOM}>Custom URL</option>
                    </select>
                  </div>
                </div>

                {promoCtaType === CTA_TYPES.CUSTOM && (
                  <div className={css.fieldGroup}>
                    <label className={css.fieldLabel}>Link URL</label>
                    <input
                      type="url"
                      className={css.input}
                      value={promoCustomUrl}
                      onChange={e => setPromoCustomUrl(e.target.value)}
                      placeholder="https://market.viveka.world/l/your-program-listing-id"
                    />
                    {/* Warning when label is set but URL is empty — the
                        backend template will suppress the button without
                        both, and a button-less promo is usually not what
                        the coach intended. */}
                    {promoCtaLabel && !(promoCustomUrl || '').trim() ? (
                      <p className={css.hintWarn}>
                        Add a URL — your button won't appear in the email without one.
                      </p>
                    ) : (
                      <p className={css.hintMuted}>
                        Tip: paste the link to one of your Viveka programs for the highest conversion.
                      </p>
                    )}
                  </div>
                )}

                {promoCtaType && promoCtaType !== CTA_TYPES.CUSTOM && previewCtaUrl && (
                  <p className={css.urlPreviewHint}>
                    Will link to: <span>{previewCtaUrl}</span>
                  </p>
                )}

                <div className={css.fieldGroup}>
                  <label className={css.fieldLabel}>Button colour</label>
                  <div className={css.accentRow}>
                    <input
                      type="color"
                      className={css.accentInput}
                      value={promoAccent}
                      onChange={e => setPromoAccent(e.target.value)}
                    />
                    <span className={css.accentHex}>{promoAccent}</span>
                    {promoAccent !== DEFAULT_ACCENT && (
                      <button
                        type="button"
                        className={css.accentReset}
                        onClick={() => setPromoAccent(DEFAULT_ACCENT)}
                      >
                        Reset to default
                      </button>
                    )}
                  </div>
                </div>

                {(promoTitle || promoBody || promoCtaLabel) && (
                  <div className={css.promoPreviewWrap}>
                    <p className={css.sectionLabel}>Preview</p>
                    <div className={css.promoPreview}>
                      {promoTitle && <p className={css.promoPreviewTitle}>{promoTitle}</p>}
                      {promoBody && (
                        <p className={css.promoPreviewBody} style={{ marginBottom: (promoCtaLabel && previewCtaUrl) ? '14px' : 0 }}>
                          {promoBody}
                        </p>
                      )}
                      {/* Mirror the email template's hasCta check exactly:
                          both a label AND a resolved URL are required for
                          the button to render. */}
                      {promoCtaLabel && previewCtaUrl && (
                        <span style={{ display: 'inline-block', background: promoAccent, color: '#fff', fontSize: '14px', fontWeight: 600, padding: '10px 20px', borderRadius: '6px' }}>
                          {promoCtaLabel}
                        </span>
                      )}
                      {promoCtaLabel && !previewCtaUrl && (
                        <p className={css.promoPreviewNoBtn}>
                          Button "{promoCtaLabel}" won't appear — pick a destination first.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className={css.actionBar}>
                  <button
                    className={css.btnSave}
                    onClick={() => save().then(() => notifySuccess('Saved'))}
                  >
                    Save
                  </button>
                  <button
                    className={css.btnSendTest}
                    onClick={() => {
                      setEmailSending(true);
                      save()
                        .then(() => {
                          onSendTestCoachEmail(currentUser?.attributes?.email)
                            .then(() => { setEmailSending(false); notifySuccess('Email Sent'); })
                            .catch(() => setEmailSending(false));
                        })
                        .catch(() => setEmailSending(false));
                    }}
                    disabled={emailSending}
                  >
                    {emailSending ? <IconSpinner /> : `Send test to ${currentUser?.attributes?.email}`}
                  </button>
                </div>

              </div>
            </div>

          </div>
        </SettingsLayoutWrapper>
      </MainDashboardWrapper>
    </PageWrapper>
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return { currentUser };
};

const mapDispatchToProps = dispatch => ({
  onSendTestCoachEmail: email => dispatch(sendTestCoachEmail(email)),
  getSdk: () => dispatch((dispatch, getState, sdk) => sdk),
});

const CoachBrandingPage = compose(connect(mapStateToProps, mapDispatchToProps))(CoachBranding);

export default CoachBrandingPage;
